import * as React from "react";
import { Copy } from "lucide-react";
import { useAction, useMutation } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { ENV } from "@/env";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { useCarriersWithBlockStatus, useUserPreferences, useAccessorials } from "@/hooks/useLogistics";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { useAppLocale } from "@/lib/i18n";
import { getOrCreateSalesDeviceInstallationId } from "@/lib/sales-device";
import { m } from "@/paraglide/messages.js";

export default function LogisticsUsersPage() {
  useAppLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const users = useUsersForImpersonation();
  const accessorials = useAccessorials();
  const { authUserId } = useAuthIdentity();

  const queryUserId = searchParams.get("userId") ?? "";
  const [selectedUserId, setSelectedUserId] = React.useState(queryUserId);
  const [magicLink, setMagicLink] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const generateMagicLinkAction = useAction(api.salesActions.generateMagicLoginLink);
  const carriersQuery = useCarriersWithBlockStatus(selectedUserId || undefined);
  const carriers = React.useMemo(
    () => (Array.isArray(carriersQuery?.carriers) ? carriersQuery.carriers : []),
    [carriersQuery?.carriers],
  );
  const isLoadingCarriers = Boolean(selectedUserId) && carriersQuery === undefined;
  const preferences = useUserPreferences(selectedUserId || undefined);

  const updateBlocklistMutation = useMutation(api.logistics.updateUserCarrierBlocklist);
  const upsertPrefsMutation = useMutation(api.logistics.upsertUserPreferences);

  // Local state for blocklist & accessorials
  const [blockedCarrierIds, setBlockedCarrierIds] = React.useState<string[]>([]);
  const [selectedAccessorialIds, setSelectedAccessorialIds] = React.useState<string[]>([]);
  const [isBlocklistDirty, setIsBlocklistDirty] = React.useState(false);
  const [isAccessorialsDirty, setIsAccessorialsDirty] = React.useState(false);

  React.useEffect(() => {
    setSelectedUserId((prev) => (prev === queryUserId ? prev : queryUserId));
  }, [queryUserId]);

  React.useEffect(() => {
    if (queryUserId === selectedUserId) return;

    const next = new URLSearchParams(searchParams);
    if (selectedUserId) {
      next.set("userId", selectedUserId);
    } else {
      next.delete("userId");
    }
    setSearchParams(next, { replace: true });
  }, [queryUserId, searchParams, selectedUserId, setSearchParams]);

  const userOptions = React.useMemo(() => {
    return buildCustomerComboboxOptions(users as any[] | undefined);
  }, [users]);

  const selectedUser = React.useMemo(
    () => users?.find((user: any) => user._id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  React.useEffect(() => {
    void selectedUserId;
    setBlockedCarrierIds([]);
    setSelectedAccessorialIds([]);
    setIsBlocklistDirty(false);
    setIsAccessorialsDirty(false);
    setMagicLink("");
    setCopied(false);
  }, [selectedUserId]);

  // Sync from server when data arrives
  React.useEffect(() => {
    const blocked = carriers.filter((c: any) => c.isBlocked).map((c: any) => c._id);
    setBlockedCarrierIds(blocked);
    setIsBlocklistDirty(false);
  }, [carriers]);

  React.useEffect(() => {
    if (Array.isArray(preferences?.accessorialIds)) {
      setSelectedAccessorialIds(preferences.accessorialIds.map((id: any) => String(id)));
      setIsAccessorialsDirty(false);
      return;
    }
    if (preferences === null) setSelectedAccessorialIds([]);
  }, [preferences]);

  const toggleCarrier = React.useCallback((carrierId: string) => {
    setBlockedCarrierIds((prev) => {
      const next = prev.includes(carrierId)
        ? prev.filter((id) => id !== carrierId)
        : [...prev, carrierId];
      setIsBlocklistDirty(true);
      return next;
    });
  }, []);

  const toggleAccessorial = React.useCallback((accessorialId: string) => {
    setSelectedAccessorialIds((prev) => {
      const next = prev.includes(accessorialId)
        ? prev.filter((id) => id !== accessorialId)
        : [...prev, accessorialId];
      setIsAccessorialsDirty(true);
      return next;
    });
  }, []);

  const handleSaveBlocklist = React.useCallback(async () => {
    if (!selectedUserId) return;
    try {
      await updateBlocklistMutation({
        authUserId: authUserId!,
        userId: selectedUserId as any,
        blockedCarrierIds: blockedCarrierIds as any[],
      });
      toast.success(m.carrier_blocklist_updated());
      setIsBlocklistDirty(false);
    } catch (e: any) {
      toast.error(e.message || m.carrier_blocklist_update_failed());
    }
  }, [selectedUserId, authUserId, blockedCarrierIds, updateBlocklistMutation]);

  const handleSavePreferences = React.useCallback(async () => {
    if (!selectedUserId) return;
    try {
      await upsertPrefsMutation({
        authUserId: authUserId!,
        userId: selectedUserId as any,
        accessorialIds: selectedAccessorialIds as any[],
      });
      toast.success(m.user_preferences_saved());
      setIsAccessorialsDirty(false);
    } catch (e: any) {
      toast.error(e.message || m.user_preferences_save_failed());
    }
  }, [selectedUserId, authUserId, selectedAccessorialIds, upsertPrefsMutation]);

  const generateMagicLink = React.useCallback(async () => {
    if (!selectedUser?.email) {
      toast.error(m.select_user_first());
      return;
    }
    setGenerating(true);
    try {
      const result = await generateMagicLinkAction({
        targetEmail: selectedUser.email,
        siteUrl: ENV.SITE_URL || undefined,
        deviceInstallationId: getOrCreateSalesDeviceInstallationId(),
      });
      if (result.success && result.url) {
        setMagicLink(result.url);
        setCopied(false);
        toast.success(m.magic_link_generated());
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.magic_link_generate_failed());
      setMagicLink("");
    } finally {
      setGenerating(false);
    }
  }, [selectedUser, generateMagicLinkAction]);

  const copyMagicLink = React.useCallback(async () => {
    if (!magicLink) return;
    await navigator.clipboard.writeText(magicLink);
    setCopied(true);
    toast.success(m.link_copied_to_clipboard());
    setTimeout(() => setCopied(false), 1500);
  }, [magicLink]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{m.user_shipping_settings_title()}</h1>
        <p className="text-muted-foreground mt-1">{m.user_shipping_settings_description()}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{m.select_user_title()}</CardTitle>
          <CardDescription>{m.select_user_description()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Label>{m.user()}</Label>
            <div className="mt-2">
              <ComboboxSelect
                options={userOptions}
                value={selectedUserId}
                onValueChange={setSelectedUserId}
                placeholder={m.search_and_select_user_placeholder()}
                searchPlaceholder={m.search_by_name_or_email_placeholder()}
                emptyText={m.no_users_found()}
                loading={users === undefined}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedUserId ? (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                  <CardTitle>{m.default_accessorials_title()}</CardTitle>
                  <CardDescription>{m.default_accessorials_description()}</CardDescription>
                </div>
                <Button onClick={() => void handleSavePreferences()} disabled={!isAccessorialsDirty}>{m.save_preferences()}</Button>
              </CardHeader>
            <CardContent>
              {accessorials ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {accessorials.map((acc: any) => {
                    const accId = acc.id ?? acc._id;
                    const checkboxId = `accessorial-${accId}`;
                    const isSelected = selectedAccessorialIds.includes(accId);
                    return (
                      <label
                        htmlFor={checkboxId}
                        key={accId}
                        className="flex items-start gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isSelected}
                          onCheckedChange={() => toggleAccessorial(accId)}
                          className="mt-0.5"
                        />
                        <span className="text-sm leading-snug">{acc.name}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{m.loading_accessorials()}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                  <CardTitle>{m.carrier_blocklist_title()}</CardTitle>
                  <CardDescription>{m.carrier_blocklist_description()}</CardDescription>
                </div>
                <Button onClick={() => void handleSaveBlocklist()} disabled={!isBlocklistDirty}>{m.save_blocklist()}</Button>
              </CardHeader>
            <CardContent>
              {!isLoadingCarriers ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
                  {carriers.map((carrier: any) => {
                    const checkboxId = `carrier-${carrier._id}`;
                    const isBlocked = blockedCarrierIds.includes(carrier._id);
                    return (
                      <label
                        htmlFor={checkboxId}
                        key={carrier._id}
                        className="flex items-center gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isBlocked}
                          onCheckedChange={() => toggleCarrier(carrier._id)}
                        />
                        <span className={`text-sm ${isBlocked ? "line-through text-destructive" : ""}`}>
                          {carrier.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{m.loading_carriers()}</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
